// Tiny IndexedDB wrapper for pending performance-video uploads.
// Stores the blob so a tab refresh, network blip, or upload failure during a
// gig doesn't lose the recording. Phase 1D companion to the upload pipeline.

export interface PendingUpload {
  historyId: number;
  songTitle: string;
  artist: string;
  userName: string;
  extension: 'mp4' | 'webm';
  contentType: string;
  blob: Blob;
  createdAt: number;
  attempts: number;
  lastError?: string;
}

const DB_NAME = 'rodeoke';
const STORE = 'pendingUploads';
const VERSION = 1;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'historyId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function savePending(item: PendingUpload): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(item);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function listPending(): Promise<PendingUpload[]> {
  const db = await openDb();
  const items = await new Promise<PendingUpload[]>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result as PendingUpload[]);
    req.onerror = () => reject(req.error);
  });
  db.close();
  return items;
}

export async function removePending(historyId: number): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(historyId);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}
